import type { Rule } from '../api'
import { Explain } from './Explain'

/**
 * Where a rule came from — TECHNICAL_DESIGN.md §5.6.
 *
 * *"A limit displayed on a portfolio that nobody wrote against that portfolio,
 * with no indication of where it came from, is worse than the spreadsheet — at
 * least a cell has an author."*
 *
 * Rendered for **every** rule, not only inherited ones. A badge that appears
 * conditionally trains the reader to see its absence as "no badge yet" rather
 * than as "this one is the portfolio's own".
 *
 * **It says which fund, not "this portfolio".** The old wording was written from
 * the component's point of view — *this* being the one whose page you happen to
 * be on — and read as a label whose subject the reader had to reconstruct. Two
 * words that name a thing the reader is looking at, arranged so they do not say
 * what the thing is, is the worst kind of caption. *Written for this fund* says
 * the same fact as a sentence, and the `?` carries what the distinction is for:
 * a rule the fund's own mandate imposes, against one it picked up by belonging
 * to a group — which is the difference between "your contract says" and "firm
 * policy says", and the reader cannot find the second on the mandate document.
 */
export function Origin({ rule }: { rule: Rule }) {
  return rule.inherited ? (
    <span className="origin origin-inherited" data-testid="origin">
      inherited from <b>{rule.origin}</b>
      <Explain term="inherited">
        {' '}— this limit is not in the fund's own mandate. It applies because the
        fund belongs to a group that carries it, so it binds every fund in that
        group and cannot be removed from this one alone.
      </Explain>
    </span>
  ) : (
    <span className="origin origin-own" data-testid="origin">
      written for this fund
      <Explain term="written for this fund">
        {' '}— the fund's own limit, recorded against it directly rather than
        picked up from a group it belongs to.
      </Explain>
    </span>
  )
}

/**
 * §7.1: a narrative rule is reviewed text, not a checkable constraint. Marking
 * it matters more in the first cut than it will later — the backlog is sized and
 * visible (launch gate 7) rather than an invisible gap.
 */
export function Checkability({ rule }: { rule: Rule }) {
  if (rule.machine_checkable) return null
  return (
    <span className="narrative" data-testid="narrative">
      narrative — text, not a threshold
    </span>
  )
}
